// 3 Nov 2026 (RK): first-party journey tracking for the admin CRM. Records which pages people open, how long
// they stay (visible time only), what they click, ebook reading and lesson plays. Sent in small batches with
// navigator.sendBeacon to /api/t. Never records what people type, and drops secret tokens from URLs.
import { API } from "./api";
import { newEventId } from "./track";

const VID_KEY = "bp_vid"; // same id the Blueprint page already uses, so ebook visits and orders join up
let queue = [];
let current = null; // { path, shownAt, visibleMs }
let timer = null;
let started = false;

export function visitorId() {
    try {
        let v = localStorage.getItem(VID_KEY);
        if (!v) { v = newEventId(); localStorage.setItem(VID_KEY, v); }
        return v;
    } catch { return "anon"; }
}

function sessionId() {
    try {
        const now = Date.now();
        let s = sessionStorage.getItem("mv_sid");
        const last = Number(sessionStorage.getItem("mv_sid_at") || 0);
        if (!s || now - last > 30 * 60 * 1000) { s = newEventId(); sessionStorage.setItem("mv_sid", s); }
        sessionStorage.setItem("mv_sid_at", String(now));
        return s;
    } catch { return ""; }
}

const off = () => typeof window === "undefined" || /^\/admin/.test(window.location.pathname);

function utm() {
    try {
        const q = new URLSearchParams(window.location.search);
        const o = {};
        ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid", "gclid"].forEach((k) => { if (q.get(k)) o[k] = q.get(k); });
        return o;
    } catch { return {}; }
}

let firstUtm = null;
let firstRef = null;

function flush(useBeacon = true) {
    if (!queue.length) return;
    const body = JSON.stringify({ vid: visitorId(), sid: sessionId(), ref: firstRef || "", utm: firstUtm || {}, events: queue.splice(0, 60) });
    try {
        if (useBeacon && navigator.sendBeacon) {
            navigator.sendBeacon(`${API}/t`, new Blob([body], { type: "text/plain" }));
        } else {
            fetch(`${API}/t`, { method: "POST", body, headers: { "Content-Type": "text/plain" }, keepalive: true, credentials: "include" }).catch(() => {});
        }
    } catch { /* tracking must never break the site */ }
    if (queue.length) flush(useBeacon);
}

export function journey(type, label = "", extra = {}) {
    if (off()) return;
    queue.push({ type, path: window.location.pathname + window.location.search, label: String(label || "").slice(0, 160), ...extra });
    if (!timer) timer = setTimeout(() => { timer = null; flush(false); }, 4000);
}

function closePage() {
    if (!current) return;
    const ms = current.visibleMs + (document.visibilityState === "visible" ? Date.now() - current.shownAt : 0);
    queue.push({ type: "leave", path: current.path, ms: Math.round(ms) });
    current = null;
}

export function journeyPage() {
    if (off()) return;
    if (firstUtm === null) { firstUtm = utm(); firstRef = document.referrer || ""; }
    const path = window.location.pathname + window.location.search;
    if (current && current.path === path) return;
    closePage();
    current = { path, shownAt: Date.now(), visibleMs: 0 };
    journey("page");
}

function labelOf(el) {
    const t = el.getAttribute("data-track") || el.getAttribute("aria-label") || el.innerText || el.getAttribute("title") || el.getAttribute("href") || "";
    return t.replace(/\s+/g, " ").trim().slice(0, 80);
}

export function startJourney() {
    if (started || typeof window === "undefined") return;
    started = true;
    document.addEventListener("click", (e) => {
        const el = e.target && e.target.closest && e.target.closest("a,button,[role=button],[data-track]");
        if (!el) return;
        const label = labelOf(el);
        if (label) journey("click", label);
    }, true);
    document.addEventListener("visibilitychange", () => {
        if (!current) return;
        if (document.visibilityState === "hidden") {
            current.visibleMs += Date.now() - current.shownAt;
            const keep = current;
            closePage();          // count the time so far ...
            flush(true);
            current = { ...keep, shownAt: Date.now(), visibleMs: 0, resumed: true }; // ... and keep timing if they come back
        } else {
            current.shownAt = Date.now();
        }
    });
    window.addEventListener("pagehide", () => { closePage(); flush(true); });
}
